/**
 * PANTONE® print specs on a brand color.
 *
 * ── Hex and Pantone are two different answers ──
 *
 * `BrandColor.hex` is the SCREEN color and stays the source of truth for
 * anything painted — swatches, previews, the hero gradient. `BrandColor.pantone`
 * is what gets PRINTED. Neither derives from the other, and `pantone.hex` (the
 * book's own screen approximation, recorded when the swatch was picked) will
 * not equal the brand's hex. That is not a discrepancy to reconcile or to
 * "fix" by painting from the Pantone value.
 *
 * ── Why there is so little in here ──
 *
 * The catalogue lives on the backend: ~13,000 swatches across eight books, with
 * CIEDE2000 matching against an arbitrary hex. Which book a code belongs to,
 * how close a swatch is, and what the printed code normalises to are all
 * answered there, on write and on read. So this file is only the display side —
 * the labels and bands a rendered ref needs when the live catalogue row isn't
 * to hand.
 */

/**
 * The eight books, as the backend enum spells them.
 *
 * A plain union rather than the generated enum, for the reason the rest of this
 * package gives: it takes no GraphQL dependency, so the two apps can map their
 * own queries onto it. The values are identical to `PantoneBook`.
 */
export type PantoneBookId =
	| 'pantoneSolidCoated'
	| 'pantoneSolidUncoated'
	| 'pantoneColorBridgeCoated'
	| 'pantoneColorBridgeUncoated'
	| 'pantoneExtendedGamutCoated'
	| 'pantoneMetallicsSolidCoated'
	| 'pantonePastelsNeonsCoated'
	| 'pantonePastelsNeonsUncoated';

export type PantoneFinish = 'coated' | 'uncoated';

/**
 * Titles for a ref whose catalogue row didn't resolve.
 *
 * `getPantoneBooks` and `PantoneColor.bookTitle` are the real source, and a
 * picker should use them. This map exists for the other case: a saved ref whose
 * `color` came back null because the book isn't seeded here or the code was
 * retired in a later edition. The stored book and code are still a correct
 * record of what the brand specified, so it renders as one — with a title,
 * rather than a raw enum value.
 */
export const PANTONE_BOOK_TITLES: Record<PantoneBookId, string> = {
	pantoneSolidCoated: 'Solid Coated',
	pantoneSolidUncoated: 'Solid Uncoated',
	pantoneColorBridgeCoated: 'Color Bridge Coated',
	pantoneColorBridgeUncoated: 'Color Bridge Uncoated',
	pantoneExtendedGamutCoated: 'Extended Gamut Coated',
	pantoneMetallicsSolidCoated: 'Metallics Solid Coated',
	pantonePastelsNeonsCoated: 'Pastels & Neons Coated',
	pantonePastelsNeonsUncoated: 'Pastels & Neons Uncoated',
};

export function pantoneBookTitle(book: string): string {
	return PANTONE_BOOK_TITLES[book as PantoneBookId] ?? book;
}

/**
 * What a picker searches unless the user widens it.
 *
 * Matching across all eight returns the Color Bridge and Extended Gamut
 * variants of the same ink, which reads as four near-identical rows and helps
 * nobody. Coated and uncoated are the two a print buyer actually chooses
 * between, and the same ink genuinely prints differently on each.
 */
export const DEFAULT_PANTONE_BOOKS: PantoneBookId[] = [
	'pantoneSolidCoated',
	'pantoneSolidUncoated',
];

/* ────────────────────────── how close is it ────────────────────────── */

export type PantoneAccuracy = 'exact' | 'close' | 'acceptable' | 'distant';

/**
 * The band, not the number.
 *
 * `deltaE` comes back on every match and is the wrong thing to put in front of
 * a print buyer — the band is what they can act on. Two cautions the backend
 * guide is explicit about, and that this copy is written to respect:
 *
 * `exact` means the book's SCREEN value is identical, NOT that the ink will
 * reproduce the color perfectly. So it is never phrased as a promise about
 * print.
 *
 * `distant` is real information rather than a failure — it means no spot ink
 * reaches this color, which is the normal answer for neons and bright greens.
 * The honest UI says so instead of presenting the nearest row confidently.
 */
export const PANTONE_ACCURACY: Record<
	PantoneAccuracy,
	{ label: string; hint: string; tone: 'success' | 'warn' | 'danger' }
> = {
	exact: {
		label: 'Exact',
		hint: 'The book prints this same screen value.',
		tone: 'success',
	},
	close: {
		label: 'Close',
		hint: 'A viewer would call it the same colour.',
		tone: 'success',
	},
	acceptable: {
		label: 'Acceptable',
		hint: 'Recognisably the same colour — worth comparing side by side.',
		tone: 'warn',
	},
	distant: {
		label: 'Distant',
		hint: 'Nothing nearer exists in these books — this colour is likely outside what spot ink reaches.',
		tone: 'danger',
	},
};

/* ────────────────────────── the stored ref ────────────────────────── */

/**
 * `BrandColor.pantone`, as the sections render it.
 *
 * Identity is `{ book, code }` — never the catalogue row id. Row ids are not
 * stable: re-seeding a book replaces its rows wholesale and every id changes,
 * so one must never be persisted, cached across sessions, or sent back as a
 * reference.
 */
export interface BrandPantone {
	book: PantoneBookId | string;
	/** As printed in the book, suffix included — `186 C`, `Bright Red C`. */
	code: string;
	/** The book's screen approximation, recorded when the swatch was picked.
	 * Optional, and deliberately not equal to the brand's own hex. */
	hex?: string | null;
	/** Resolved catalogue row, when the book is seeded and the code current. */
	color?: { bookTitle?: string | null; finish?: string | null } | null;
}

/** "PANTONE 186 C" — the way the code is written wherever it is shown alone. */
export function pantoneLabel(pantone: Pick<BrandPantone, 'code'>): string {
	return `PANTONE ${pantone.code}`;
}

/** The book under the code. Prefers the live row's own title, falls back to the
 * map above when the ref no longer resolves. */
export function pantoneBookLabel(pantone: BrandPantone): string {
	return pantone.color?.bookTitle ?? pantoneBookTitle(String(pantone.book));
}
